import React, { useState, useEffect } from 'react';
import { Card, Typography, Image, Space, Modal } from 'antd';
import { 
  LinkOutlined, 
  AppstoreOutlined, 
  FileOutlined, 
  PlayCircleOutlined,
  VideoCameraOutlined,
  FileImageOutlined
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * 获取OSS预览URL
 * @param {string} ossUrl OSS URL
 * @returns {Promise<string>} 可访问的预览URL
 */
const getPreviewUrl = async (ossUrl) => {
  if (!ossUrl) return null;
  
  // 如果已经是HTTP URL，直接返回
  if (ossUrl.startsWith('http')) {
    return ossUrl;
  }
  
  // 如果是OSS协议URL，请求转换
  if (ossUrl.startsWith('oss://')) {
    try {
      const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(ossUrl)}`);
      const data = await response.json();
      
      if (data.success && data.url) {
        return data.url;
      }
    } catch (error) {
      console.error('获取预览URL失败:', error);
    }
  }
  
  return null;
};

/**
 * 图片预览组件
 */
const ImagePreview = ({ imageInfo, size = 50 }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreviewUrl = async () => {
      if (!imageInfo?.url) {
        setLoading(false);
        return;
      }

      // 如果是HTTP URL，直接使用
      if (imageInfo.url.startsWith('http')) {
        setPreviewUrl(imageInfo.url);
        setLoading(false);
        return;
      }

      // 如果是OSS URL，需要转换
      if (imageInfo.url.startsWith('oss://')) {
        try {
          const response = await fetch(`/api/media/wework/oss/preview?url=${encodeURIComponent(imageInfo.url)}`);
          const data = await response.json();
          
          if (data.success && data.url) {
            setPreviewUrl(data.url);
          } else {
            console.error('获取图片预览URL失败:', data.message);
          }
        } catch (error) {
          console.error('获取图片预览URL异常:', error);
        }
      } else {
        // 其他情况直接使用
        setPreviewUrl(imageInfo.url);
      }
      
      setLoading(false);
    };

    fetchPreviewUrl();
  }, [imageInfo?.url]);

  if (loading) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px'
      }}>
        <FileImageOutlined style={{ fontSize: size * 0.3, color: '#ccc' }} />
      </div>
    );
  }

  if (!previewUrl) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px'
      }}>
        <FileImageOutlined style={{ fontSize: size * 0.3, color: '#ccc' }} />
      </div>
    );
  }

  return (
    <Image 
      width={size} 
      height={size} 
      src={previewUrl}
      style={{ objectFit: 'cover', borderRadius: '4px' }}
      placeholder={
        <div style={{ 
          width: size, 
          height: size, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: '#f0f0f0'
        }}>
          <FileImageOutlined style={{ fontSize: size * 0.3, color: '#ccc' }} />
        </div>
      }
    />
  );
};

/**
 * 视频预览组件
 */
const VideoPreview = ({ videoInfo, size = 50 }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const videoRef = React.useRef(null);

  useEffect(() => {
    const fetchPreviewUrl = async () => {
      if (videoInfo?.url) {
        const url = await getPreviewUrl(videoInfo.url);
        setPreviewUrl(url);
      }
      setLoading(false);
    };
    
    fetchPreviewUrl();
  }, [videoInfo?.url]);

  const handleVideoClick = () => {
    if (previewUrl) {
      setIsModalVisible(true);
    }
  };

  // 关闭模态框时暂停视频
  const handleModalClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // 重置到开始位置
    }
    setIsModalVisible(false);
  };

  if (loading) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>加载中</Text>
      </div>
    );
  }

  return (
    <>
      <div 
        style={{ 
          width: size, 
          height: size, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          background: '#f0f0f0',
          borderRadius: '4px',
          position: 'relative',
          overflow: 'hidden',
          cursor: previewUrl ? 'pointer' : 'default'
        }}
        onClick={handleVideoClick}
      >
        {previewUrl ? (
          <>
            <video
              src={previewUrl}
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain' 
              }}
              muted
              controls={false}
            />
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              background: 'rgba(0,0,0,0.1)'
            }}>
              <PlayCircleOutlined style={{ fontSize: size * 0.4, color: '#fff' }} />
            </div>
          </>
        ) : (
          <VideoCameraOutlined style={{ fontSize: size * 0.4, color: '#666' }} />
        )}
      </div>

      {/* 视频预览模态框 */}
      <Modal
        title="视频预览"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        centered
        destroyOnClose={true}
      >
        {previewUrl && (
          <video
            ref={videoRef}
            src={previewUrl}
            controls
            style={{ 
              width: '100%', 
              maxHeight: '70vh'
            }}
            autoPlay
            onEnded={() => {
              // 视频播放结束时也重置时间
              if (videoRef.current) {
                videoRef.current.currentTime = 0;
              }
            }}
          />
        )}
      </Modal>
    </>
  );
};

/**
 * 小程序预览组件
 */
const MiniProgramPreview = ({ miniprogramInfo, size = 50 }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreviewUrl = async () => {
      // 尝试从不同的字段获取封面图片URL
      const imageUrl = miniprogramInfo?.picUrl || miniprogramInfo?.coverUrl || miniprogramInfo?.thumbUrl;
      if (imageUrl) {
        const url = await getPreviewUrl(imageUrl);
        setPreviewUrl(url);
      }
      setLoading(false);
    };
    
    fetchPreviewUrl();
  }, [miniprogramInfo]);

  if (loading) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>加载中</Text>
      </div>
    );
  }

  return (
    <div style={{ 
      width: size, 
      height: size, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#f0f0f0',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      {previewUrl ? (
        <Image 
          src={previewUrl}
          alt="小程序封面"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover' 
          }}
          preview={false}
        />
      ) : (
        <AppstoreOutlined style={{ fontSize: size * 0.4, color: '#666' }} />
      )}
    </div>
  );
};

/**
 * 链接预览组件
 */
const LinkPreview = ({ linkInfo, size = 50 }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPreviewUrl = async () => {
      if (linkInfo?.picUrl) {
        const url = await getPreviewUrl(linkInfo.picUrl);
        setPreviewUrl(url);
      }
      setLoading(false);
    };
    
    fetchPreviewUrl();
  }, [linkInfo?.picUrl]);

  if (loading) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f0f0f0',
        borderRadius: '4px'
      }}>
        <Text type="secondary" style={{ fontSize: '12px' }}>加载中</Text>
      </div>
    );
  }

  return (
    <div style={{ 
      width: size, 
      height: size, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#f0f0f0',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      {previewUrl ? (
        <Image 
          src={previewUrl}
          alt="链接封面"
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover' 
          }}
          preview={false}
        />
      ) : (
        <LinkOutlined style={{ fontSize: size * 0.4, color: '#666' }} />
      )}
    </div>
  );
};

/**
 * 文件预览组件
 */
const FilePreview = ({ size = 50 }) => {
  return (
    <div style={{ 
      width: size, 
      height: size, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#f0f0f0',
      borderRadius: '4px'
    }}>
      <FileOutlined style={{ fontSize: size * 0.4, color: '#666' }} />
    </div>
  );
};

/**
 * 文本预览组件
 */
const TextPreview = ({ textContent, size = 50 }) => {
  if (!textContent || textContent.trim() === '') {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#f8f9fa',
        borderRadius: '4px',
        border: '1px solid #e0e0e0',
        fontSize: '12px',
        color: '#999'
      }}>
        暂无内容
      </div>
    );
  }

  // 计算合适的字体大小
  const fontSize = Math.max(14, Math.min(14, size / 6));
  const maxLength = Math.floor((size - 8) / fontSize * 4); // 估算可容纳的字符数
  const displayText = textContent.length > maxLength 
    ? textContent.substring(0, maxLength - 3) + '...' 
    : textContent;

  return (
    <div style={{ 
      width: size, 
      height: size, 
      display: 'flex', 
      alignItems: 'flex-start', 
      justifyContent: 'flex-start',
      background: '#fff',
      borderRadius: '4px',
      border: '1px solid #e0e0e0',
      padding: '4px',
      fontSize: `${fontSize}px`,
      color: '#333',
      lineHeight: '1.3',
      overflow: 'hidden',
      wordBreak: 'break-all'
    }}>
      {displayText}
    </div>
  );
};

/**
 * 素材预览组件
 * @param {Object} material 素材对象
 * @param {number} size 预览尺寸
 */
const MaterialPreview = ({ material, size = 100 }) => {
  if (!material) {
    return (
      <div style={{ 
        width: size, 
        height: size, 
        background: '#f0f0f0',
        borderRadius: '4px'
      }} />
    );
  }

  switch (material.materialType) {
    case 'IMAGE':
      return <ImagePreview imageInfo={material.imageInfo} size={size} />;

    case 'VIDEO':
      return <VideoPreview videoInfo={material.videoInfo} size={size} />;

    case 'MINIPROGRAM':
      return <MiniProgramPreview miniprogramInfo={material.miniprogramInfo} size={size} />;

    case 'LINK':
      return <LinkPreview linkInfo={material.linkInfo} size={size} />;

    case 'FILE':
      return <FilePreview size={size} />;

    case 'TEXT':
      return <TextPreview textContent={material.textContent} size={size} />;

    default:
      return (
        <div style={{ 
          width: size, 
          height: size, 
          background: '#f0f0f0',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>未知</Text>
        </div>
      );
  }
};

export default MaterialPreview; 